import { useCommonUseOfFundsListSuspense } from "@boundless/bl-api-client/common/services"
import {
  type CustomerFundingRequests,
  FundingNeedsStatusEnum,
} from "@boundless/bl-api-client/customer/models"
import { useCustomerCompanyRetrieveSuspense } from "@boundless/bl-api-client/customer/services"
import { Link, useNavigate } from "@tanstack/react-router"
import {
  ApertureIcon,
  BanknoteArrowDownIcon,
  BlocksIcon,
  BoxIcon,
  Building2Icon,
  FileUserIcon,
  MagnetIcon,
  PiggyBankIcon,
  SquarePercentIcon,
  WashingMachineIcon,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { formatCurrency } from "@/lib/forms/utils"
import { formatDate, getCurrentStepPath } from "@/lib/utils"

interface FundingRequestCardProps {
  item: CustomerFundingRequests
  companyUuid: string
}

export function FundingRequestCard({
  item,
  companyUuid,
}: FundingRequestCardProps) {
  const navigate = useNavigate()
  const { data: useOfFundsData } = useCommonUseOfFundsListSuspense()

  const useOfFundsIcons = {
    Marketing: MagnetIcon,
    "Purchase Inventory": BoxIcon,
    "New Equipment": WashingMachineIcon,
    "Payroll Capital": BanknoteArrowDownIcon,
    "Buy a Business": Building2Icon,
    "Refinance Debt": BlocksIcon,
    "Working Capital": PiggyBankIcon,
    "Revenue Advances": SquarePercentIcon,
    "Grant Applications": FileUserIcon,
    Other: ApertureIcon,
  }

  const useOfFundsName =
    useOfFundsData.find((fund) => fund.id === item.useOfFunds)?.name || "Other"

  const UseOfFundsIcon =
    useOfFundsIcons[useOfFundsName as keyof typeof useOfFundsIcons] ??
    ApertureIcon

  const { data: company } = useCustomerCompanyRetrieveSuspense(
    companyUuid ?? "",
  )

  const getFundingReqStatusColor = (status: string | undefined) => {
    switch (status) {
      case "live":
        return "bg-green-100 text-green-800"
      case "expired":
        return "bg-yellow-100 text-yellow-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const onButtonClick = () => {
    if (item.hasApplications) {
      navigate({ to: "/applications" })
    } else if (!item.isSigned) {
      navigate({
        to: `/funding-requests/$fundingNeedsUuid/lender-match`,
        params: { fundingNeedsUuid: item.uuid },
      })
    }
  }

  const getButtonCopy = () => {
    if (item.hasApplications) {
      return "View Applications"
    } else if (!item.isSigned) {
      return "Finish Application"
    }
    return "---"
  }

  const continueTo = getCurrentStepPath(
    company.currentStep,
    company.latestUnsubmittedApplicationUuid,
    company.latestSubmittedFundingNeedsUuid,
  )

  const ContinueButton = () => (
    <Button
      className="text-primary border-primary w-full border bg-gray-50 font-medium hover:bg-gray-100"
      variant="secondary"
      asChild
    >
      <Link {...continueTo}>Continue</Link>
    </Button>
  )

  const ApplyMoreButton = ({
    fundingNeedsUuid,
  }: {
    fundingNeedsUuid: string
  }) => (
    <Button
      onClick={() =>
        navigate({
          to: "/funding-requests/$fundingNeedsUuid/lender-match",
          params: { fundingNeedsUuid },
        })
      }
      className="text-primary border-primary w-full border bg-gray-50 font-medium hover:bg-gray-100"
      variant="secondary"
    >
      Apply to More Lenders
    </Button>
  )

  return (
    <Card className="border-0 bg-gray-50 shadow-sm">
      <CardHeader className="flex content-center items-center gap-3">
        <UseOfFundsIcon className="h-8 w-8 text-gray-700" />
        <div className="flex h-[2lh] items-center text-xl leading-none font-medium text-gray-900">
          {useOfFundsName}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="mb-1 text-xs text-gray-500">Funding Requested</p>
            <p className="font-semibold text-gray-900">
              {formatCurrency(item.capitalAmount.toString())}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="mb-1 text-xs text-gray-500">Created On</p>
            <p className="text-sm text-gray-700">
              {formatDate(item.createdAt)}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="mb-1 text-xs text-gray-500">Status</p>
            <Badge
              variant="secondary"
              className={`px-2 py-1 text-xs capitalize ${getFundingReqStatusColor(item.status)}`}
            >
              {item.status}
            </Badge>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="mb-1 text-xs text-gray-500">Credit Application</p>
            <div className="flex items-center gap-1">
              <div
                className={`h-2 w-2 rounded-full ${item.isSigned ? "bg-green-500" : item.isReadyToBeSigned ? "bg-yellow-400" : ""}`}
              />
              {item.isSigned ? (
                <span className="text-sm text-gray-700">Signed</span>
              ) : item.isReadyToBeSigned && item.unsubmittedApplication ? (
                <Link
                  className="text-primary hover:underline"
                  to="/applications/$applicationUuid/sign"
                  params={{ applicationUuid: item.unsubmittedApplication.uuid }}
                >
                  Sign
                </Link>
              ) : (
                <span>---</span>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={onButtonClick}
          className="text-primary border-primary w-full border bg-gray-50 font-medium hover:bg-gray-100"
          variant="secondary"
        >
          {getButtonCopy()}
        </Button>
        {item.status === FundingNeedsStatusEnum.live &&
          (item.unsubmittedApplication ? (
            <ContinueButton />
          ) : (
            <ApplyMoreButton fundingNeedsUuid={item.uuid} />
          ))}
      </CardContent>
    </Card>
  )
}
